import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { API_LOGIN_REQUEST } from './msalConfig';

/** Returns a callable that yields a fresh access token for the API. */
export function useToken() {
  const { instance, accounts } = useMsal();

  return useCallback(async (): Promise<string> => {
    const account = accounts[0];
    if (!account) throw new Error('No signed-in account');
    try {
      const result = await instance.acquireTokenSilent({ ...API_LOGIN_REQUEST, account });
      return result.accessToken;
    } catch (err) {
      // Fall back to interactive auth when silent acquisition can't
      // complete. InteractionRequiredAuthError is the documented case
      // (consent / MFA / expired session). BrowserAuthError covers the
      // hidden-iframe failure modes — notably monitor_window_timeout,
      // raised when the silent-renewal iframe can't finish loading the
      // redirect URI. Without this second branch an iframe failure
      // throws straight past the catch and the app hangs with no token
      // and no recovery, even after a reload. Genuine network/server
      // errors are neither type and still propagate.
      if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
        const result = await instance.acquireTokenPopup({ ...API_LOGIN_REQUEST, account });
        return result.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);
}
